import type { BoardOrder } from './board'
import { mayCloseFromBoard } from './dispatch-rules'
import { isHandedOverInPerson, type ChannelLike } from './waiter-assign'

/**
 * *Vydaná* on a counter pickup takes the payment - `FRONTEND.md` §9.740, owner O111.
 *
 * **The owner**: *"in vydaná it should be available to use the popup if its hotovosť, karta, which karta... And
 * assign it to someone, who use Vydaná button"*. Decided when asked: the till's own *Zaplatiť*, which prints the
 * receipt if none is out yet and closes the order as *Doručená*, owned by whoever pressed it.
 *
 * **Which press opens it**: the board's own in-house close (`mayCloseFromBoard` - on the premises, at `ready` or
 * `quality_check`), on an order handed over in person (`isHandedOverInPerson` - a counter, not a table), **whose money
 * is still to be taken at the counter**. Two kinds are not, and close the way the board always closed them:
 *
 * - **`card_online`, unless the bank refused it** (`online_failed`). Paid, there is nothing to collect and the
 *   receipt is the payment flow's; still in flight, a counter payment would be a second one when the bank answers.
 *   The till's dialog offers neither method, so it would also re-record the order as cash.
 * - **`card_courier`** - promised to the courier's terminal. The dialog cannot say *Karta kuriér* either, and
 *   re-recording it as cash would contradict a receipt the board may already have printed as card (review of
 *   §9.740, confirmed on 9 of Šurany's 1 744 pickups).
 */
export function paysAtTheCounter(order: BoardOrder, channels?: readonly ChannelLike[]): boolean {
  if (!mayCloseFromBoard(order, channels)) return false
  if (!isHandedOverInPerson(order, channels)) return false
  const method = (order.paymentMethod ?? '').trim().toLowerCase()
  if (method === 'card_courier') return false
  const status = (order.paymentStatus ?? '').trim().toLowerCase()
  if (method === 'card_online') return status === 'online_failed'
  return status !== 'online_paid'
}

/**
 * The roles the till's close admits whatever their permissions - `split-payment`'s `orRoles`, `BACKEND.md`
 * §10A.375. Spelled here because the board decides before the request whether to offer the dialog at all.
 */
export const TILL_ROLES: readonly string[] = ['main_admin', 'admin', 'waiter']

/** The screens whose permission opens the till's close - `split-payment`'s `@RequirePermission` value. */
export const TILL_PERMISSIONS: readonly string[] = ['new_order', 'restaurant']

/**
 * Whether this person may take a payment the way the till does - `FRONTEND.md` §9.740.
 *
 * **`split-payment`'s own guard, asked before the press** rather than discovered after it: the dialog prints the
 * receipt first and closes second, so somebody the close refuses would be left with a document out and the order
 * still open. Anybody else keeps the board's close, which needs no payment and is what they had before §9.740.
 */
export function takesPaymentHere(
  systemRole: string | undefined,
  hasPermission: (key: string) => boolean,
): boolean {
  if (TILL_ROLES.includes(systemRole ?? '')) return true
  return TILL_PERMISSIONS.some((key) => hasPermission(key))
}
